export const RESTAURANT_RATING_MIN = 0;
export const RESTAURANT_RATING_MAX = 5;

export function parseRestaurantRating(value: unknown): number | null {
  if (value == null || value === '') return null;
  if (typeof value === 'string' && value.trim() === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return n;
}

export function isRestaurantRatingProvided(value: unknown): boolean {
  return value != null && value !== '' && !(typeof value === 'string' && value.trim() === '');
}

export function isValidRestaurantRating(value: unknown): boolean {
  if (!isRestaurantRatingProvided(value)) return false;
  const n = parseRestaurantRating(value);
  if (n == null) return false;
  return n >= RESTAURANT_RATING_MIN && n <= RESTAURANT_RATING_MAX;
}

/** Returns normalized rating in 0–5, or null when missing or out of range. */
export function normalizeRestaurantRating(value: unknown): number | null {
  const n = parseRestaurantRating(value);
  if (n == null) return null;
  if (n < RESTAURANT_RATING_MIN || n > RESTAURANT_RATING_MAX) return null;
  return Math.round(n * 10) / 10;
}

export function formatRestaurantRatingDisplay(value: unknown): string {
  const normalized = normalizeRestaurantRating(value);
  if (normalized === 0) return 'N/A';
  if (normalized != null) return normalized.toFixed(1);
  if (parseRestaurantRating(value) == null) return 'N/A';
  return 'Invalid';
}

export function getRestaurantRatingState(value: unknown): {
  valid: boolean;
  display: string;
  message?: string;
} {
  if (!isRestaurantRatingProvided(value)) {
    return { valid: true, display: 'N/A' };
  }
  const normalized = normalizeRestaurantRating(value);
  // 0 means no ratings yet — do not show a misleading "0.0"
  if (normalized === 0) {
    return { valid: true, display: 'N/A' };
  }
  if (normalized != null) {
    return { valid: true, display: normalized.toFixed(1) };
  }
  const raw = parseRestaurantRating(value);
  if (raw == null) {
    return {
      valid: false,
      display: 'Invalid',
      message: 'Rating must be a number between 0 and 5.',
    };
  }
  return {
    valid: false,
    display: 'Invalid',
    message: `Rating must be between ${RESTAURANT_RATING_MIN} and ${RESTAURANT_RATING_MAX} (received ${raw}).`,
  };
}
